// No iOS browser — Safari, Chrome, or otherwise — implements `beforeinstallprompt`;
// they all run on WebKit, which has never shipped it, so there's no automatic
// "Install app?" banner to hook into on iOS. The only path to installing is the manual
// Share -> "Add to Home Screen" flow, so this nudges an iOS visitor there instead of
// leaving them with no way to discover it. Chromium (Android/desktop/ChromeOS) gets its
// own first-run dialog instead — see SharedCommon/pwaInstall.ts + PwaInstallDialog.tsx.
import i18n from '../i18n';

const DISMISSED_KEY = 'pwa_ios_install_prompt_dismissed_v1';
const BANNER_ID = 'pwa-ios-install-banner';

function isIosDevice(): boolean {
  const nav = window.navigator;
  if (/iPhone|iPod|iPad/.test(nav.userAgent)) {
    return true;
  }
  // iPadOS 13+ reports its UA as a desktop Mac; multi-touch is the tell.
  return nav.platform === 'MacIntel' && nav.maxTouchPoints > 1;
}

function isRunningStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function dismiss(): void {
  document.getElementById(BANNER_ID)?.remove();
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // Best-effort only; worst case the prompt reappears next visit.
  }
}

export function installIosInstallPrompt(): void {
  if (!isIosDevice() || isRunningStandalone() || wasDismissed()) {
    return;
  }

  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.style.cssText =
    'position:fixed;left:0;right:0;bottom:0;z-index:9999;padding:12px 16px;' +
    'background:#323130;color:#fff;font-size:14px;text-align:center;';

  const text = document.createElement('span');
  text.textContent = i18n.t(
    'Pwa.install.ios',
    'Install this app: tap the Share icon, then "Add to Home Screen".'
  );
  banner.appendChild(text);

  const dismissButton = document.createElement('button');
  dismissButton.textContent = i18n.t('Pwa.install.dismiss', 'Got it');
  dismissButton.style.cssText =
    'margin-left:12px;background:transparent;color:#fff;border:1px solid #fff;' +
    'border-radius:4px;padding:2px 10px;cursor:pointer;';
  dismissButton.onclick = dismiss;
  banner.appendChild(dismissButton);

  document.body.appendChild(banner);
}
